import type { DrawingDocument, NormalizedPoint } from '../model';
import { isChemicalBond } from './documentSelectors.ts';
import { nextSequentialId, replaceTopology, roundCoordinate } from './drawingDocumentCore.ts';
import { drawingBondLength, spreadAngles } from './explicitHydrogens.ts';

/**
 * #736 step 1: a functional group put on the drawing as its real atoms and bonds, scaled to the
 * drawing's bond length. `group` is laid out with a stand-in atom on its bonding side (its first
 * atom; the group's own first atom is its second): the stand-in's bond is lined up with the bond the
 * group really attaches by, so the group keeps proper angles to it, and of the two mirror images
 * the one further from the drawing's other atoms is used. Nothing outside the group moves.
 *
 * - on a terminal or lone atom: that atom becomes the group's first atom, the stand-in's bond along
 *   the bond from its one neighbour (Cl clicked with CF₃ becomes CF₃);
 * - on any other atom: the group bonds to it, in the widest gap between its bonds;
 * - on empty paper: the group on its own, its first atom where clicked (capped with hydrogen).
 */
export function placeFunctionalGroup(
  document: DrawingDocument,
  group: DrawingDocument,
  target: { readonly atomId: string } | { readonly point: NormalizedPoint }
): DrawingDocument {
  return placeFunctionalGroupAt(document, group, target).document;
}

/**
 * #736 step 2: {@link placeFunctionalGroup}, telling where the group went -- its first atom
 * (`attachmentId`) and the atom it bonds to (`restId`; none for a group on its own) -- so it can be
 * drawn contracted at once.
 */
export function placeFunctionalGroupAt(
  document: DrawingDocument,
  group: DrawingDocument,
  target: { readonly atomId: string } | { readonly point: NormalizedPoint }
): { readonly document: DrawingDocument; readonly attachmentId?: string; readonly restId?: string } {
  const { structure } = document;
  const { coordinateSet } = document.depiction;
  const coordinates = coordinateSet.coordinates;
  const [standIn, first] = group.structure.graph.nodes;
  if (!standIn || !first) return { document };
  const groupNodes = group.structure.graph.nodes.slice(1);
  const groupEdges = group.structure.graph.edges.filter(
    (edge) => edge.source !== standIn.id && edge.target !== standIn.id
  );
  const groupAt = group.depiction.coordinateSet.coordinates;
  const origin = groupAt[first.id]!;
  const behind = groupAt[standIn.id]!;
  const length = drawingBondLength(document);
  const scale = length / Math.max(drawingBondLength(group), 1e-6);
  // The group's own axis: from the stand-in to its first atom.
  const ownAngle = Math.atan2(origin.y - behind.y, origin.x - behind.x);

  // Where the first atom goes, the direction the bond into it runs, and what it replaces or bonds to.
  let anchor: { x: number; y: number };
  let direction: number;
  let replaced: string | null = null;
  let bondTo: string | null = null;
  let restId: string | undefined;
  if ('atomId' in target) {
    const at = coordinates[target.atomId];
    if (!at || !structure.atoms[target.atomId]) return { document };
    const neighbours = structure.graph.edges.flatMap((edge) => {
      if (!isChemicalBond(structure.bonds[edge.id])) return [];
      if (edge.source === target.atomId) return [edge.target];
      if (edge.target === target.atomId) return [edge.source];
      return [];
    });
    if (neighbours.length <= 1) {
      replaced = target.atomId;
      restId = neighbours[0];
      anchor = at;
      const neighbour = neighbours[0] ? coordinates[neighbours[0]] : undefined;
      direction = neighbour ? Math.atan2(at.y - neighbour.y, at.x - neighbour.x) : 0;
    } else {
      bondTo = target.atomId;
      restId = target.atomId;
      const taken = neighbours.flatMap((id) => {
        const other = coordinates[id];
        return other ? [Math.atan2(other.y - at.y, other.x - at.x)] : [];
      });
      direction = spreadAngles(taken, 1)[0]!;
      anchor = { x: at.x + Math.cos(direction) * length, y: at.y + Math.sin(direction) * length };
    }
  } else {
    anchor = { x: target.point.x * coordinateSet.width, y: target.point.y * coordinateSet.height };
    direction = 0;
  }

  // Each group atom's place, the group turned onto `direction` (and mirrored across it, for `flip`).
  const turn = direction - ownAngle;
  const layout = (flip: boolean) =>
    new Map(
      groupNodes.map((node) => {
        const point = groupAt[node.id]!;
        let dx = (point.x - origin.x) * scale,
          dy = (point.y - origin.y) * scale;
        // Mirror across the group's own axis before turning it.
        if (flip) {
          const along = dx * Math.cos(ownAngle) + dy * Math.sin(ownAngle);
          const across = -dx * Math.sin(ownAngle) + dy * Math.cos(ownAngle);
          dx = along * Math.cos(ownAngle) + across * Math.sin(ownAngle);
          dy = along * Math.sin(ownAngle) - across * Math.cos(ownAngle);
        }
        return [
          node.id,
          {
            x: roundCoordinate(anchor.x + dx * Math.cos(turn) - dy * Math.sin(turn)),
            y: roundCoordinate(anchor.y + dx * Math.sin(turn) + dy * Math.cos(turn))
          }
        ] as const;
      })
    );
  const others = Object.entries(coordinates)
    .filter(([id]) => id !== replaced)
    .map(([, point]) => point);
  const room = (placed: ReadonlyMap<string, { x: number; y: number }>) =>
    Math.min(
      Infinity,
      ...[...placed.values()].flatMap((point) =>
        others.map((other) => Math.hypot(point.x - other.x, point.y - other.y))
      )
    );
  const straight = layout(false);
  const mirrored = layout(true);
  const groupPlaces = room(mirrored) > room(straight) + 1e-6 ? mirrored : straight;

  // New ids for the group's atoms and bonds; its first atom is the replaced atom, if any.
  const nodes = [...structure.graph.nodes];
  const edges = [...structure.graph.edges];
  const atoms = { ...structure.atoms };
  const bonds = { ...structure.bonds };
  const formalCharges = { ...structure.formalCharges };
  const placed = { ...coordinates };
  const idOf = new Map<string, string>();
  for (const node of groupNodes) {
    const id =
      node.id === first.id && replaced
        ? replaced
        : nextSequentialId(
            nodes.map((each) => each.id),
            'n'
          );
    idOf.set(node.id, id);
    if (id !== replaced) nodes.push({ id });
    atoms[id] = { ...group.structure.atoms[node.id]! };
    formalCharges[id] = group.structure.formalCharges[node.id] ?? 0;
    placed[id] =
      node.id === first.id ? { x: roundCoordinate(anchor.x), y: roundCoordinate(anchor.y) } : groupPlaces.get(node.id)!;
  }
  for (const edge of groupEdges) {
    const id = nextSequentialId(
      edges.map((each) => each.id),
      'e'
    );
    edges.push({ id, source: idOf.get(edge.source)!, target: idOf.get(edge.target)! });
    bonds[id] = { ...group.structure.bonds[edge.id]! };
  }
  if (bondTo) {
    const id = nextSequentialId(
      edges.map((each) => each.id),
      'e'
    );
    edges.push({ id, source: bondTo, target: idOf.get(first.id)! });
    bonds[id] = { order: 1, type: 'covalent' };
  }
  const placedDocument = replaceTopology(
    document,
    { ...structure.graph, revision: structure.graph.revision + 1, nodes, edges },
    atoms,
    bonds,
    formalCharges,
    placed,
    // A replaced atom's own label (an R label, say) goes; the group's atoms speak for themselves.
    replaced
      ? Object.fromEntries(Object.entries(document.depiction.atomLabels).filter(([id]) => id !== replaced))
      : document.depiction.atomLabels,
    document.depiction.bondLengthOverrides
  );
  return { document: placedDocument, attachmentId: idOf.get(first.id)!, ...(restId ? { restId } : {}) };
}
